// utils/exportData.js - Enhanced version with Excel export
import * as XLSX from 'xlsx';

// Function to convert table data to Excel format
export const exportToExcel = (data, filename = 'data', sheetName = 'Sheet1') => {
  try {
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, `${filename}.xlsx`);
    
    console.log(`Excel file "${filename}.xlsx" downloaded successfully`);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

// Enhanced exportData function with Excel support
export const exportData = (data, filename = 'export', format = 'csv') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('No data available for export');
      return;
    }

    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9_-]/g, '_');

    switch (format.toLowerCase()) {
      case 'excel':
      case 'xlsx':
        exportToExcel(data, sanitizedFilename);
        break;
        
      case 'csv':
        exportToCSV(data, sanitizedFilename);
        break;
        
      default:
        console.warn(`Unsupported format: ${format}. Using CSV instead.`);
        exportToCSV(data, sanitizedFilename);
    }
  } catch (error) {
    console.error('Export failed:', error);
  }
};

// New function specifically for table data export from SortableTable
export const exportTableToExcel = (headings, rawData, filename = 'table_data') => {
  try {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      console.warn('No table data available for export');
      return;
    }

    // Create formatted data with proper column names using display names
    const formattedData = rawData.map(row => {
      const formattedRow = {};
      headings.forEach(heading => {
        const displayName = getDisplayName(heading);
        formattedRow[displayName] = row[heading] || '';
      });
      return formattedRow;
    });

    const timestamp = new Date().toISOString().split('T')[0];
    const finalFilename = `${filename}_${timestamp}`;
    
    exportToExcel(formattedData, finalFilename);
  } catch (error) {
    console.error('Error exporting table to Excel:', error);
  }
};

// New function for chatbot response data export from PrayasBot
export const exportChatbotDataToExcel = (chatbotOutput, index, filename = 'chatbot_data') => {
  try {
    let dataToExport = [];
    let finalFilename = filename;
    
    const timestamp = new Date().toISOString().split('T')[0];
    
    // Check for ground_data first (priority)
    if (chatbotOutput[index]?.ground_data && Array.isArray(chatbotOutput[index]?.ground_data)) {
      dataToExport = chatbotOutput[index].ground_data;
      finalFilename = `${filename}_Source_Data_${timestamp}`;
    } 
    // Then check for table data
    else if (chatbotOutput[index]?.tableHeading && chatbotOutput[index]?.output?.json_data) {
      try {
        const parsedData = JSON.parse(chatbotOutput[index].output.json_data);
        if (Array.isArray(parsedData)) {
          dataToExport = parsedData;
          finalFilename = `${filename}_Table_Data_${timestamp}`;
        }
      } catch (e) {
        console.error("Error parsing JSON data for Excel export:", e);
        return;
      }
    }

    if (dataToExport.length > 0) {
      exportToExcel(dataToExport, finalFilename);
    } else {
      console.warn("No valid data found for Excel export");
    }
  } catch (error) {
    console.error('Error exporting chatbot data to Excel:', error);
  }
};

// CSV export function (keeping your existing functionality)
const exportToCSV = (data, filename) => {
  try {
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle values that contain commas, quotes, or newlines
          if (value && (value.toString().includes(',') || value.toString().includes('"') || value.toString().includes('\n'))) {
            return `"${value.toString().replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`CSV file "${filename}.csv" downloaded successfully`);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw error;
  }
};



// Helper function to get display names (copied from SortableTable)
function getDisplayName(heading) {
  const lowerHeading = heading.toLowerCase();
  
  // Handle specific cases
  if (lowerHeading.includes('monthname') || lowerHeading.includes('month_name')) {
    return 'Month';
  }
  if (lowerHeading.includes('statename') || lowerHeading.includes('state_name')) {
    return 'State';
  }
  if (lowerHeading.includes('districtname') || lowerHeading.includes('district_name')) {
    return 'District';
  }
  
  // Handle camelCase and snake_case conversion
  const cleaned = heading
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
  
  return cleaned;
}